import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore
import { loadReCaptcha } from 'react-recaptcha-google';
import { ICustomMap } from 'types/ICustomMap';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { setWorkspace } from 'store/workSpace/actions';
import {
    logOutAction,
    removeCurrentAccountFromRecentAuthAction,
    setAuthedUser,
    setRecentAuthsAction,
    setUserTokensAction,
} from 'store/authentication/actions';
import { useHistory, useLocation } from 'react-router-dom';
import { publicRoutes, Roles } from 'config/general';
import { useQuery, useQueryClient } from 'react-query';
import { billingStateQueryKey } from 'pages/Billing/config';
import { requestBillingState } from 'pages/Billing/services';
import { setBillingAccountInfo } from 'store/billing/actions';
import routes from 'config/routes';
import { requestAuthAccount } from 'pages/CommandCenter/Accounts/services';
import { authAccountQueryKey } from 'pages/CommandCenter/Accounts/config';
import { userProfileQueryKey } from 'pages/Users/config';
import { requestGerUserProfile } from 'pages/Users/services';
import { multiAccountAutoLoginQueryKey } from 'components/NavSideBar/config';
import { fetchMultiAccountAutoLogin } from 'components/NavSideBar/services';
import { cacheRecentAuths, cacheUserTokens, getCachedRecentAuths, getCachedUserTokens } from 'utils/auth';
import parseJwt from 'utils/parseJwt';
import { toast } from 'react-toastify';
import { textsCommon } from 'texts/index';
import useRegisterPushMessaging from 'hooks/useRegisterPushMessaging';
// import useSupportGpt from 'hooks/useSupportGpt';
import useAnalytics from 'hooks/useAnalytics';
import { getUserRingtoneName } from 'utils/ringtone';

interface IUseApp {
    isLoggedIn: boolean;
    isLoading: boolean;
    isAdmin: boolean;
    isAccountRestricted: boolean;
    isNotRestrictedPage: boolean;
}

const reCaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const useApp = (): IUseApp => {
    const { isLoggedIn } = useSelector(({ authentication }: ICustomMap) => authentication || {});
    const queryClient = useQueryClient();
    const currentWorkspace = useSelector(({ workspace }: ICustomMap) => workspace);
    const { isAccountRestricted } = useSelector(({ billing }: ICustomMap) => billing);

    const ldClient = useLDClient();

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();

    const isPublicRoute = useMemo(() => publicRoutes.includes(location.pathname), [location]);

    const { data: accountData, isLoading: isLoadingAccount } = useQuery({
        queryKey: authAccountQueryKey,
        queryFn: () => requestAuthAccount(),
        staleTime: 0,
        enabled: isLoggedIn && !isPublicRoute,
        retry: 2,
        onSuccess: ({ respData }) => {
            dispatch(setWorkspace(respData || null));
        },
        onError: () => {
            toast.error(textsCommon.failedAccountLogin);
            const tokens = getCachedUserTokens();
            const { company_id: companyId } = parseJwt(tokens?.access_token || '');
            dispatch(removeCurrentAccountFromRecentAuthAction(companyId || ''));
            history.push(routes.auth.enterWorkspace);
        },
    });

    const workspace = accountData?.respData;

    const { data: profileData, isLoading: isLoadingProfile } = useQuery({
        queryKey: [userProfileQueryKey, workspace?.id_str],
        queryFn: () => requestGerUserProfile(),
        staleTime: 0,
        retry: 2,
        enabled: isLoggedIn && !isPublicRoute && !!workspace?.id_str,
        onSuccess: ({ respData }) => {
            const ringtone = getUserRingtoneName(respData?.id_str || '');
            const res = respData ? { ...respData, ringtone } : null;
            dispatch(setAuthedUser(res));
        },
    });

    useQuery({
        queryKey: multiAccountAutoLoginQueryKey,
        queryFn: fetchMultiAccountAutoLogin,
        enabled: isLoggedIn && !isPublicRoute && !!workspace?.id_str,
        onSuccess: ({ respData }) => {
            const currentFreshTokens = respData?.find(({ workspace_id_str }) => workspace_id_str === workspace?.id_str);

            // window.console.log(`Auto Login response at ${new Date().toUTCString()}`, JSON.stringify(respData));
            // window.console.log(
            //     `Auto Login fresh account ${workspace?.id_str} tokens at ${new Date().toUTCString()}`,
            //     JSON.stringify(currentFreshTokens),
            // );

            // update current user tokens in local storage
            if (currentFreshTokens?.access_token && currentFreshTokens?.refresh_token) {
                cacheUserTokens({
                    access_token: currentFreshTokens.access_token,
                    refresh_token: currentFreshTokens.refresh_token,
                });
                dispatch(
                    setUserTokensAction({
                        access_token: currentFreshTokens.access_token,
                        refresh_token: currentFreshTokens.refresh_token,
                    }),
                );
            } else if (!currentFreshTokens?.access_token) {
                window.console.error('Auto Login - no access token found for workspace: ', workspace?.id_str);
            } else if (!currentFreshTokens?.refresh_token) {
                window.console.error('Auto Login - no refresh token found for workspace: ', workspace?.id_str);
            }

            const recentAuths = getCachedRecentAuths() || {};

            respData?.forEach(
                ({
                    workspace_id_str,
                    user_id_str,
                    common_user_id,
                    access_token,
                    refresh_token,
                    workspace_display_name,
                    workspace_name,
                    identity_provider,
                }) => {
                    recentAuths[workspace_id_str] = {
                        ...(recentAuths[workspace_id_str] || {}),
                        id_str: workspace_id_str,
                        userId: user_id_str,
                        commonUserId: common_user_id,
                        shortName: workspace_display_name,
                        workspaceName: workspace_name,
                        access_token: access_token || '',
                        refresh_token: refresh_token || '',
                        identityProvider: identity_provider,
                        timestamp: workspace_id_str === workspace?.id_str ? Date.now() : recentAuths[workspace_id_str]?.timestamp || null,
                    };
                },
            );
            if (recentAuths) {
                cacheRecentAuths(recentAuths);
                dispatch(setRecentAuthsAction(recentAuths));
            }
        },
    });

    const user = profileData?.respData;
    const isAdmin = useMemo(() => user?.role === Roles.admin, [user]);

    // fetch account billing state
    useQuery({
        queryKey: [billingStateQueryKey, { company_id: workspace?.id_str || '' }],
        queryFn: requestBillingState,
        staleTime: 0,
        enabled: !!workspace?.id_str && isAdmin,
        onSuccess: ({ respData }) => {
            dispatch(setBillingAccountInfo(respData || {}));
        },
    });

    useAnalytics();
    useRegisterPushMessaging();

    const isCommandCenterWorkspaceDetails = useMemo(
        () => location.pathname.includes('/command-center/workspaces/detail/'),
        [location.pathname],
    );

    // const { initSupportGpt } = useSupportGpt();
    //
    // useEffect(() => {
    //     if (isLoggedIn) {
    //         initSupportGpt();
    //     }
    // }, [initSupportGpt, isLoggedIn]);

    // Signing out must empty the query cache. Logout and login are in-app navigations, so the
    // cache outlives the session; without this, the next account to sign in in the same tab can be
    // served the previous one's cached data by any screen whose query key omits the account.
    useEffect(() => {
        if (!isLoggedIn) {
            queryClient.clear();
        }
    }, [isLoggedIn, queryClient]);

    useEffect(() => {
        if (ldClient && user?.id_str && currentWorkspace?.id_str && !isCommandCenterWorkspaceDetails) {
            ldClient.identify({
                key: currentWorkspace.id_str,
                custom: {
                    user_id: user.id_str,
                },
            });
        }
    }, [user, ldClient, currentWorkspace, dispatch, isCommandCenterWorkspaceDetails]);

    useEffect(() => {
        if (reCaptchaSiteKey) {
            loadReCaptcha(reCaptchaSiteKey);
        }
    }, []);

    useEffect(() => {
        if (isPublicRoute) {
            dispatch(logOutAction());
            dispatch(setWorkspace(null));
        }
    }, [dispatch, isPublicRoute]);

    useEffect(() => {
        // nav to cached route if logged in
        const navigationUrl = localStorage.getItem('navigationUrl');
        if (isLoggedIn && navigationUrl) {
            localStorage.removeItem('navigationUrl');
            history.replace(navigationUrl);
        }
    }, [isLoggedIn, history]);

    const isNotRestrictedPage = useMemo(
        () => location.pathname.includes(routes.billing.root) || location.pathname.includes(routes.auth.enterWorkspace),
        [location],
    );

    const isSwiftlaneUser = useMemo(() => user?.email?.includes('swiftlane.com'), [user]);

    return useMemo(
        () => ({
            isLoading: isLoadingAccount || isLoadingProfile,
            isAdmin,
            isLoggedIn,
            isAccountRestricted: isAccountRestricted && !isSwiftlaneUser,
            isNotRestrictedPage,
        }),
        [isLoadingAccount, isLoadingProfile, isAdmin, isLoggedIn, isAccountRestricted, isSwiftlaneUser, isNotRestrictedPage],
    );
};

export default useApp;
